import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES } from "../lib/finance";
import { Button } from "./ui/button";
import { Sun, Moon, LogOut, Sparkles, Wallet, Crown } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const links = [
  { to: "/", label: "Overview", tid: "nav-link-landing" },
  { to: "/calendar", label: "Calendar", tid: "nav-link-calendar" },
  { to: "/bills", label: "Bills", tid: "nav-link-bills" },
  { to: "/income", label: "Income", tid: "nav-link-income" },
  { to: "/paid", label: "Mark Paid", tid: "nav-link-mark-paid" },
];

export default function Header() {
  const { user, logout, updatePrefs } = useAuth();
  const nav = useNavigate();
  const currency = user?.prefs?.currency || "USD";
  const theme = user?.prefs?.theme || "light";
  const stickers = user?.prefs?.stickers_enabled ?? true;

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const setCurrency = (v) => updatePrefs({ currency: v });
  const toggleTheme = () => updatePrefs({ theme: theme === "dark" ? "light" : "dark" });
  const toggleStickers = () => updatePrefs({ stickers_enabled: !stickers });

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border/80">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Wallet size={18} />
          </div>
          <div className="font-display font-extrabold text-xl tracking-tight leading-none">
            Bill<span className="text-[hsl(var(--primary))]">·</span>System
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.tid}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          {user?.subscription && !user.subscription.is_premium && (
            <Button asChild variant="default" size="sm" className="hidden sm:inline-flex" data-testid="header-upgrade-btn">
              <NavLink to="/pricing"><Crown size={13} className="mr-1"/> Upgrade</NavLink>
            </Button>
          )}
          {user?.subscription && !user.subscription.is_premium && (
            <Button asChild variant="default" size="icon" className="sm:hidden h-9 w-9" data-testid="header-upgrade-btn-mobile">
              <NavLink to="/pricing" aria-label="Upgrade to Premium"><Crown size={15}/></NavLink>
            </Button>
          )}
          {user?.subscription?.is_premium && user.subscription.status === "active" && (
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[hsl(var(--payday-bg))] border border-[hsl(var(--payday-border))] text-[hsl(var(--payday-text))] text-[10px] uppercase tracking-widest font-newspaper" data-testid="header-premium-badge">
              <Crown size={11}/> Premium
            </span>
          )}
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="header-currency-selector" className="w-[82px] sm:w-[110px] h-9 px-2 sm:px-3 text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(CURRENCIES).map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="ghost" size="icon" onClick={toggleTheme} data-testid="header-theme-toggle"
            aria-label="Toggle theme" className="hidden sm:inline-flex"
          >
            {theme === "dark" ? <Sun size={18}/> : <Moon size={18}/>}
          </Button>

          <Button
            variant={stickers ? "default" : "outline"} size="icon" onClick={toggleStickers}
            data-testid="cute-stickers-toggle" aria-label="Toggle stickers" className="hidden sm:inline-flex"
          >
            <Sparkles size={16}/>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 sm:hidden font-bold" data-testid="header-user-menu-mobile" aria-label="User menu">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="max-w-[220px] truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme} data-testid="menu-toggle-theme">
                {theme === "dark" ? <Sun size={14} className="mr-2"/> : <Moon size={14} className="mr-2"/>} {theme === "dark" ? "Light mode" : "Dark mode"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleStickers} data-testid="menu-toggle-stickers">
                <Sparkles size={14} className="mr-2"/> {stickers ? "Hide stickers" : "Show stickers"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout-mobile">
                <LogOut size={14} className="mr-2"/> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-9 max-w-[180px] hidden sm:inline-flex" data-testid="header-user-menu">
                <span className="truncate">{user?.name || user?.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="max-w-[220px] truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout">
                <LogOut size={14} className="mr-2"/> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="md:hidden border-t border-border/60 flex gap-1 px-2 py-2 overflow-x-auto">
        {links.map((l) => (
          <NavLink
            key={l.to} to={l.to} end={l.to === "/"} data-testid={`${l.tid}-mobile`}
            className={({ isActive }) =>
              `px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
      </div>
    </header>
  );
}
